import { css, CSSResult } from 'lit'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { csColorBackground, csColorBorder, csColorText } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { SCREENS } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'


export const cscComparePage = {
  backgroundColor: css`var(--csc-compare-page-background-color, ${csColorBackground.primary})`,

  /** --csc-compare-page-border-color is the pre-rename name, kept as a fallback. */
  tableBorderColor: css`var(--csc-compare-page-table-border-color, var(--csc-compare-page-border-color, ${csColorBorder.light}))`,
  rowPaddingY: css`var(--csc-compare-page-row-padding-y, ${csSpacing.spacing05})`,
  rowPaddingX: css`var(--csc-compare-page-row-padding-x, ${csSpacing.spacing05})`,

  groupHeaderTextColor: css`var(--csc-compare-page-group-header-text-color, ${csColorText.body})`,
  sectionGap: css`var(--csc-compare-page-section-gap, ${csSpacing.spacing08})`,

  /**
   * Alternating row backgrounds (odd row, then even row, per table).
   * --csc-table-row-background-color-primary/-secondary are the pre-rename names, kept as fallbacks.
   */
  rowBackgroundPrimary: css`var(--csc-compare-page-row-background-color-odd, var(--csc-table-row-background-color-primary, ${csColorBackground.primary}))`,
  rowBackgroundSecondary: css`var(--csc-compare-page-row-background-color-even, var(--csc-table-row-background-color-secondary, ${csColorBackground.secondary}))`,
}

export const style: CSSResult = css`
  :host {
    display: block;
    --csc-compare-product-count: 4;
  }

  @media (min-width: ${SCREENS.medium}) {
    :host {
      margin-block: ${csSpacing.spacing10} ${csSpacing.spacing10};
    }

    :host([compare-empty]) {
      margin-block: ${csSpacing.spacing05} ${csSpacing.spacing10};
    }
  }

  .page {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${cscComparePage.sectionGap};
    background-color: ${cscComparePage.backgroundColor};
    position: relative;
    /* Active count is set inline when loaded; falls back to max slot count (e.g. loading). */
    --csc-compare-layout-cols: var(--csc-compare-active-product-count, var(--csc-compare-product-count));
    /* Label column width must match table colgroup + tile sheet gutter (same % of row). */
    --csc-compare-label-col-width: var(--csc-compare-page-label-col-width, 22%);
  }

  .page.empty {
    align-items: flex-start;
    box-sizing: border-box;
  }

  .empty-description {
    display: block;
    width: fit-content;
    max-width: 60ch;
  }

  /**
   * Fixed preview shell: viewport position + slide-in. cse-sticky-bar inside supplies DS chrome
   * (background, padding, shadow); host sticky is disabled there because the shell is already fixed.
   * --csc-compare-page-sticky-top-offset is set on the host from sticky-top-offset and/or
   * sticky-header-selector (legacy templates used .header offsetHeight).
   * Shell spans the full viewport; inner grid is offset to match .scroll-region.
   * Hidden past table end; reappears when the last table row is visible again.
   */
  .sticky-sentinel {
    height: 1px;
    width: 100%;
    margin: 0;
    padding: 0;
    pointer-events: none;
    /* opacity:0 keeps layout for geometry/IO; visibility:hidden can make intersection flaky in some themes. */
    opacity: 0;
    flex-shrink: 0;
    align-self: stretch;
  }

  .table-end-sentinel {
    height: 1px;
    width: 100%;
    margin: 0;
    padding: 0;
    pointer-events: none;
    opacity: 0;
    flex-shrink: 0;
  }

  .sticky-strip {
    position: fixed;
    top: var(--csc-compare-page-sticky-top-offset, 0px);
    left: 0;
    right: 0;
    width: 100%;
    box-sizing: border-box;
    z-index: var(--csc-compare-page-sticky-z-index, 101);
  }

  .sticky-strip:not(.visible):not(.dismissing) {
    opacity: 0;
    transform: translateY(-32px);
    visibility: hidden;
    pointer-events: none;
  }

  .sticky-strip.visible {
    pointer-events: auto;
    animation: sticky-bar-enter 300ms ease forwards;
  }

  .sticky-strip.dismissing {
    pointer-events: none;
    animation: sticky-bar-exit 300ms ease forwards;
  }

  @keyframes sticky-bar-enter {
    from {
      transform: translateY(-32px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes sticky-bar-exit {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-32px);
      opacity: 0;
    }
  }

  .sticky-strip[hidden] {
    display: none !important;
  }

  /* Design-system bar chrome; positioning is on .sticky-strip (fixed). */
  .sticky-strip cse-sticky-bar {
    display: block;
    width: 100%;
    position: static;
    top: auto;
    box-sizing: border-box;
    /* Keep preview columns aligned with the tile row / table below (no horizontal inset). */
    --cse-sticky-bar-padding-x: 0;
  }

  .sticky-inner {
    display: grid;
    box-sizing: border-box;
    width: var(--csc-compare-page-sticky-content-width, 100%);
    margin-left: var(--csc-compare-page-sticky-content-margin-left, 0px);
    align-items: stretch;
    grid-template-columns: var(--csc-compare-label-col-width, 22%) repeat(
      var(--csc-compare-product-count),
      minmax(0, 1fr)
    );
    overflow-x: hidden;
    overflow-y: visible;
  }

  .gutter {
    min-width: 0;
  }

  .sticky-products {
    grid-column: 2 / -1;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(var(--csc-compare-product-count), minmax(0, 1fr));
    column-gap: ${csSpacing.spacing08};
    align-items: stretch;
  }

  .sticky-col {
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .sticky-col.removing {
    animation: sticky-preview-exit 300ms ease forwards;
    overflow: hidden;
  }

  @keyframes sticky-preview-exit {
    from {
      opacity: 1;
      max-height: 200px;
    }
    to {
      opacity: 0;
      max-height: 0;
    }
  }

  .sticky-col.placeholder {
    pointer-events: none;
  }

  .sticky-col csc-product-preview {
    display: block;
    width: 100%;
    flex: 1 1 auto;
    --csc-product-preview-row-gap: ${csSpacing.spacing04};
    --csc-product-preview-title-overflow: visible;
    --csc-product-preview-title-white-space: normal;
    --csc-product-preview-title-text-overflow: clip;
  }

  .toggle-wrap {
    width: 100%;
    align-self: stretch;
  }

  .toggle-wrap cse-toggle {
    display: inline-flex;
  }

  .header,
  .accordion-content {
    width: 100%;
    align-self: stretch;
  }

  .scroll-region {
    width: 100%;
    max-width: 100%;
    align-self: stretch;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }

  .scroll-inner {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${cscComparePage.sectionGap};
  }

  .accordion-group {
    display: block;
    width: 100%;
  }

  .tiles {
    display: grid;
    box-sizing: border-box;
    width: 100%;
    min-width: 100%;
    align-items: stretch;
    column-gap: 0;
    grid-template-columns: var(--csc-compare-label-col-width, 22%) repeat(
      var(--csc-compare-product-count),
      minmax(0, 1fr)
    );
  }

  .tile-list {
    grid-column: 2 / -1;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(var(--csc-compare-product-count), minmax(0, 1fr));
    column-gap: ${csSpacing.spacing08};
    align-items: stretch;
  }

  /* Match PLP grid: equal row height, height passes through to base tile. */
  .tile-list > .tile {
    min-width: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    /* Center rather than stretch, so the (now natural-width) image sits centered
       instead of filling the tile's width — matches csc-base-product-tile. */
    align-items: center;
    gap: ${csSpacing.spacing07};
  }

  .tile-list cse-group.tile-group {
    width: 100%;
    height: 100%;
  }

  /*
   * csc-product-image's own :host forces width:100% and a background-color (for its
   * aspect-ratio/letterboxed usages) — reset both here so it sizes to the image's own
   * natural dimensions and shows no fill color, same as inside csc-base-product-tile.
   */
  .tile > csc-product-image {
    display: block;
    width: auto;
    max-width: 100%;
    --cse-image-max-width: 100%;
    --csc-product-image-background-color: transparent;
  }

  .tile-group cse-link {
    display: block;
    width: 100%;
  }

  .tile-group csc-price {
    display: block;
    width: 100%;
  }

  .tile > csc-order-form {
    display: block;
    width: 100%;
    margin-top: auto;
  }

  /* Keep mobile behavior for tablet as well (up to large breakpoint). */
  @media (max-width: ${SCREENS.large}) {
    .sticky-inner {
      overflow-x: hidden;
      overflow-y: visible;
    }

    .gutter {
      display: none;
    }

    .tiles,
    .sticky-inner {
      grid-template-columns: 1fr;
    }

    .tile-list,
    .sticky-products {
      grid-column: 1;
      width: max(100%, calc(var(--csc-compare-layout-cols) * (100vw / 1.5)));
      grid-template-columns: repeat(var(--csc-compare-layout-cols), 1fr);
      column-gap: ${csSpacing.spacing04};
    }

    .sticky-col.placeholder {
      display: none;
    }

    .table,
    .table tbody {
      display: block;
    }

    .table colgroup {
      display: none;
    }

    .row {
      display: grid;
      grid-template-columns: repeat(var(--csc-compare-layout-cols), calc(100vw / 1.5));
      width: max(100%, calc(var(--csc-compare-layout-cols) * (100vw / 1.5)));
    }

    .cell.label {
      grid-column: 1 / -1;
      position: static;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-shadow: none;
      border-right: none;
      border-bottom: none;
    }

    .label-inner {
      position: sticky;
      left: 0;
      padding-block: ${csSpacing.spacing04} ${csSpacing.spacing02};
    }

    .cell.value {
      min-width: 0;
      text-align: left;
    }

    /* Empty slots kept for desktop table width; hidden on small viewports to avoid stray horizontal scroll. */
    .cell.placeholder {
      display: none;
    }
  }

  /* Tablet-specific tuning: keep mobile pattern, but reduce tile width. */
  @media (min-width: ${SCREENS.small}) and (max-width: ${SCREENS.large}) {
    .tile-list,
    .sticky-products {
      width: max(100%, calc(var(--csc-compare-layout-cols) * (100vw / 2.4)));
    }

    .row {
      grid-template-columns: repeat(var(--csc-compare-layout-cols), calc(100vw / 2.4));
      width: max(100%, calc(var(--csc-compare-layout-cols) * (100vw / 2.4)));
    }
  }

  .table {
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .table col.col-label {
    width: var(--csc-compare-label-col-width, 22%);
  }

  .table col.col-product {
    width: calc((100% - var(--csc-compare-label-col-width, 22%)) / var(--csc-compare-product-count));
  }


  .tile {
    position: relative;
    width: 100%;
    min-width: 0;
    min-height: 0;
  }

  .tile.removing {
    animation: sticky-preview-exit 300ms ease forwards;
    overflow: hidden;
  }

  .tile-close {
    position: absolute;
    top: ${csSpacing.spacing03};
    right: ${csSpacing.spacing03};
    z-index: 2;
  }

  .row {
    border-bottom: ${csBorder.width} solid ${cscComparePage.tableBorderColor};
  }

  /* Alternating row backgrounds: first row primary, then secondary (every table, every accordion) */
  .table tbody tr:nth-child(odd) {
    background-color: ${cscComparePage.rowBackgroundPrimary};
  }

  .table tbody tr:nth-child(even) {
    background-color: ${cscComparePage.rowBackgroundSecondary};
  }

  .cell {
    min-width: 0;
    box-sizing: border-box;
    padding: ${cscComparePage.rowPaddingY} ${cscComparePage.rowPaddingX};
    vertical-align: top;
    border-right: ${csBorder.width} solid ${cscComparePage.tableBorderColor};
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .cell cse-text {
    display: block;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
  }

  .cell:last-child {
    border-right: none;
  }

  /* Sticky label column: stays fixed on horizontal scroll; match row stripes */
  .cell.label {
    position: sticky;
    left: 0;
    z-index: 2;
    --cs-font-body-s-font-weight: 700;
    color: ${cscComparePage.groupHeaderTextColor};
    text-align: left;
    border-right: ${csBorder.width} solid ${cscComparePage.tableBorderColor};
  }

  @media(max-width: ${SCREENS.medium}) {
    .cell.label {
      padding: 0;
    }

    .label-inner {
      display: inline-block;
      padding: ${cscComparePage.rowPaddingY} ${cscComparePage.rowPaddingX};
    }
  }

  .table tbody tr:nth-child(odd) .cell.label {
    background-color: ${cscComparePage.rowBackgroundPrimary};
  }

  .table tbody tr:nth-child(even) .cell.label {
    background-color: ${cscComparePage.rowBackgroundSecondary};
  }

  .cell.value {
    text-align: center;
  }

  .check {
    color: ${csColorText.body};
  }
`
